// La pantalla del Agente (`/agente`) sin el lienzo a la vista, con la cara del Asistente de
// Crear (ADR-2026-10-16-el-agente-se-ve-como-el-asistente). Son el mismo asistente —un motor,
// un historial (ADR-2026-10-18-un-solo-historial-con-lo-de-principal)— y ahora también se ven
// igual: el fondo azul, la demo de Cumbre, la caja de Crear en modo Asistente (con
// Producto, Modelo y Referencia adentro) y las dos cards de abajo; con mensajes, la
// conversación en el medio con los globos blancos y la caja al pie. Lo único que no está es
// el interruptor Imagen / Video / Asistente: acá sólo se habla con el Asistente.
//
// La caja escribe en el MISMO borrador y los MISMOS adjuntos del chat: lo que se empieza
// en el panel lateral sigue acá y al revés.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minimize2, Plus } from 'lucide-react';
import { useChatAtlas } from '../ChatAtlasContext';
import { CajaDeRedes } from '../../pages/v2/modes/redes/CajaDeRedes';
import { DemoDelAsistente } from '../../pages/v2/modes/redes/DemoDelAsistente';
import { FondoDeCrear } from '../../pages/v2/modes/redes/FondoDeCrear';
import { HistorialDeSesiones } from '../../pages/v2/modes/redes/HistorialDeSesiones';
import { conTransicion } from '../../pages/v2/modes/redes/conTransicion';
import { rubroDeCrear } from '../../pages/v2/modes/redes/contenidoPorRubro';
import { AJUSTES_DEFAULT, type AjustesDeRedes } from '../../pages/v2/modes/redes/redes';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';
import type { VistaGrafico } from './BurbujaMensaje';
import { ChipLote, PEDIDO_POR_DEFECTO_LOTE } from './BarraEntradaChat';
import { MensajesDeLaConversacion } from './MensajesDeLaConversacion';
import { MenuChat } from './MenuChat';
import { ModalDeFuentes, type IdDeFuente } from './ModalDeFuentes';
import { PastillasDeContenido } from './PastillasDeContenido';
import { BOTON_ESQUINA_BLANCO } from './estilosAgente';
import { useEsEscritorio } from './useEsEscritorio';
import { useEntornoChatAtlas } from '../entornoContext';
import { prepararAdjunto } from './prepararAdjunto';
import { archivosEnganchados, lugarLibreAdjuntos, mensajeLimiteAdjuntos } from '../adjuntosChatAtlas';
import { VeloSoltarImagen, usePegarYSoltarImagenes } from '../../pages/v2/modes/shared/usePegarYSoltarImagenes';

export const PortadaDelAsistente: React.FC<{
  /** Qué hace la tarjeta de un gráfico: en esta pantalla, abrirlo en la columna de la derecha. */
  vistaGrafico?: VistaGrafico;
  onAbrirGrafico?: (id: string) => void;
  /** Hay un lienzo escondido: "Volver al lienzo" lo vuelve a mostrar, como en Crear. */
  onVolverAlLienzo?: () => void;
  /** Lo que la esquina de la izquierda deja libre: el logo de la sidebar en tira. */
  margenIzquierdo?: number;
}> = ({ vistaGrafico, onAbrirGrafico, onVolverAlLienzo, margenIzquierdo = 0 }) => {
  const chat = useChatAtlas();
  const navigate = useNavigate();
  const esEscritorio = useEsEscritorio();
  const { rubro } = useRubroMarcaActiva();
  const rubroDeLaPortada = rubroDeCrear(rubro);
  // La caja de Crear los pide aunque en el Asistente no generen ni se muestren.
  const [ajustes, setAjustes] = useState<AjustesDeRedes>(AJUSTES_DEFAULT);
  const [fuentes, setFuentes] = useState<IdDeFuente | null>(null);
  const [pedidoDeIrAlFondo, setPedidoDeIrAlFondo] = useState(0);

  // Pegar (Cmd/Ctrl+V) o soltar una imagen la adjunta, como en Crear
  // (ADR-2026-10-20-pegar-imagenes-en-el-asistente). Entra a los MISMOS adjuntos del chat,
  // con lo mismo que usa la caja del chat (`prepararAdjunto`). Con el selector abierto no
  // escucha: la foto caería atrás.
  const { companyId } = useEntornoChatAtlas();
  const raizRef = useRef<HTMLDivElement | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const { arrastrando } = usePegarYSoltarImagenes({
    activo: fuentes === null,
    dentroDe: raizRef,
    onImagenes: async (archivos) => {
      if (archivos.length === 0) return;
      // El mismo tope de archivos por mensaje que la caja del chat.
      const libre = lugarLibreAdjuntos(archivosEnganchados(chat.adjuntos));
      if (libre === 0) {
        setAviso(mensajeLimiteAdjuntos());
        return;
      }
      setAviso(null);
      setSubiendo(true);
      let fallo: string | null = archivos.length > libre ? mensajeLimiteAdjuntos() : null;
      for (const archivo of archivos.slice(0, libre)) {
        try {
          chat.agregarAdjunto(await prepararAdjunto(archivo, companyId));
        } catch (e) {
          fallo = fallo ?? (e instanceof Error && e.message ? e.message : 'No se pudo subir esa imagen.');
        }
      }
      setSubiendo(false);
      if (fallo) setAviso(fallo);
    },
  });

  // Con mensajes, la conversación en el medio; sin, la portada con la demo.
  const enConversacion = chat.mensajes.length > 0;

  // Una sugerencia del turno completa la caja y le da el foco; no manda
  // (ADR-2026-10-20-las-sugerencias-del-turno-completan-la-caja).
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0);
  const completarConSugerencia = (texto: string) => {
    chat.setBorrador(texto);
    setPedidoDeFoco((n) => n + 1);
  };

  // El usuario "empezó" cuando escribe, adjunta algo o ya hay conversación: la demo se achica
  // a su pastilla y Cumbre se asoma a escuchar. Mismo criterio y mismo respiro del blur que
  // Crear: tocar una sugerencia primero le saca el foco al texto.
  const [escribiendo, setEscribiendo] = useState(false);
  const blurRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (blurRef.current) window.clearTimeout(blurRef.current);
  }, []);
  const seguirElFoco = {
    onFocus: (e: React.FocusEvent) => {
      if (!(e.target instanceof HTMLTextAreaElement)) return;
      if (blurRef.current) window.clearTimeout(blurRef.current);
      setEscribiendo(true);
    },
    onBlur: (e: React.FocusEvent) => {
      if (!(e.target instanceof HTMLTextAreaElement)) return;
      if (blurRef.current) window.clearTimeout(blurRef.current);
      blurRef.current = window.setTimeout(() => setEscribiendo(false), 220);
    },
  };
  const interactuando =
    escribiendo || chat.borrador.trim() !== '' || chat.adjuntos.length > 0 || !!chat.loteFotos || enConversacion;

  // Enviar: con el Asistente respondiendo, a la cola (como la caja del chat). El primer
  // mensaje pasa de la portada a la conversación con la transición de vista: la caja VIAJA
  // del medio al pie, igual que en Crear.
  const enviar = () => {
    // Un LOTE de fotos (siete o más, desde el selector) se manda aunque no haya texto, con el
    // mismo pedido por defecto que la caja del chat (ADR-2026-10-19-review-191-el-lote-en-la-portada).
    const texto = chat.borrador.trim() || (chat.loteFotos ? PEDIDO_POR_DEFECTO_LOTE : '');
    if (!texto) return;
    if (chat.streaming) {
      chat.encolarMensaje(texto);
      return;
    }
    const mandar = () => {
      chat.enviarMensaje(texto);
      setPedidoDeIrAlFondo((n) => n + 1);
    };
    if (enConversacion) mandar();
    else conTransicion(mandar);
  };

  const caja = (
    <>
    {/* El lote no es un adjunto de la caja de Crear (`chat.adjuntos`): sin este chip, subir
        veinte fotos cerraba el selector y no quedaba nada a la vista ni cómo sacarlas. */}
    {aviso && (
      <p role="status" className="relative z-10 mb-2 text-[13px] text-red-600">
        {aviso}
      </p>
    )}
    {chat.loteFotos && (
      <div className="relative z-10 mb-2 flex">
        <ChipLote lote={chat.loteFotos} onQuitar={chat.quitarLoteFotos} />
      </div>
    )}
    <CajaDeRedes
      prompt={chat.borrador}
      onPromptChange={chat.setBorrador}
      pedidoDeFoco={pedidoDeFoco}
      onGenerar={enviar}
      adjuntos={chat.adjuntos}
      onQuitarAdjunto={chat.quitarAdjunto}
      onAbrirFuentes={setFuentes}
      ajustes={ajustes}
      onAjustes={setAjustes}
      asistente
      sinInterruptor
      onEnviarAlAsistente={enviar}
      subiendo={subiendo}
      puedeEnviarSinTexto={!!chat.loteFotos}
      ajustesDelAsistente={
        <PastillasDeContenido
          preferencias={chat.preferencias}
          onCambiar={chat.setPreferencia}
          inteligencia={chat.inteligencia}
          onCambiarInteligencia={chat.setInteligencia}
          puedeSubirInteligencia={chat.puedeSubirInteligencia}
          compactas={!esEscritorio}
        />
      }
      // Cumbre asoma sólo en la computadora, como en Crear.
      conMascota={esEscritorio}
      interactuando={interactuando}
      enConversacion={enConversacion}
      rubro={rubroDeLaPortada}
    />
    </>
  );

  return (
    <div ref={raizRef} className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <VeloSoltarImagen visible={arrastrando} />
      {/* Un solo fondo para la portada y la conversación, como en Crear: montado acá, no se
          rehace al pasar de una a la otra. */}
      <FondoDeCrear atenuado={enConversacion} />

      {/* Volver, sólo en el teléfono: la misma pastilla de Crear (chevron + logo), que lleva a
          la home. En la computadora ese lugar lo ocupa la tira de la sidebar. */}
      <button
        type="button"
        onClick={() => navigate('/interfaz-v2')}
        aria-label="Volver"
        className="absolute left-4 top-4 z-20 inline-flex h-10 items-center gap-1 rounded-full bg-white pl-2 pr-3.5 shadow-[0_10px_30px_-10px_rgba(2,6,43,0.28)] ring-1 ring-black/[0.05] active:opacity-70 md:hidden"
      >
        <span aria-hidden="true" className="material-symbols-rounded text-[20px] text-black/50">
          chevron_left
        </span>
        <img src="/images/estudio-atlas-logo-solo.svg" alt="" className="h-[20px] w-auto" />
      </button>
      {/* Las esquinas, como en Crear. A la izquierda "Nuevo chat", sólo con conversación —como
          "Nueva sesión"—, al lado de la tira de la sidebar (o del volver, en el teléfono); a la
          derecha "Volver al lienzo" si hay uno escondido, el Historial (el mismo de Crear) y, en
          la computadora, el "⋯" con Memoria, Skills y Consumo. */}
      {enConversacion && (
        <button
          type="button"
          onClick={chat.reiniciar}
          style={{ left: esEscritorio ? margenIzquierdo + 16 : 88 }}
          className={`${BOTON_ESQUINA_BLANCO} absolute top-4 z-20 transition-[left,transform]`}
        >
          <Plus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
          Nuevo chat
        </button>
      )}
      <div className="absolute right-4 top-4 z-20 flex items-center gap-2 md:right-6">
        {onVolverAlLienzo && (
          <button type="button" onClick={onVolverAlLienzo} title="Ver el lienzo con el chat al costado" className={BOTON_ESQUINA_BLANCO}>
            <Minimize2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            Volver al lienzo
          </button>
        )}
        <HistorialDeSesiones
          actual={chat.motor.getChatId() ?? undefined}
          onElegir={(id) => void chat.cargarChat(id)}
          soloConversaciones
          claseDelBoton={BOTON_ESQUINA_BLANCO}
          className="relative"
        />
        {esEscritorio && <MenuChat estilo="blanco" sinNuevoNiHistorial />}
      </div>

      {enConversacion ? (
        <div key="conversacion" className="relative flex h-full min-h-0 flex-col">
          <div className="crear-conversa relative z-10 flex min-h-0 flex-1 flex-col pt-14 md:pt-4">
            <MensajesDeLaConversacion
              anchoColumna="max-w-3xl"
              pedidoDeIrAlFondo={pedidoDeIrAlFondo}
              globosBlancos
              onSugerencia={completarConSugerencia}
              vistaGrafico={vistaGrafico}
              onAbrirGrafico={onAbrirGrafico}
            />
          </div>
          <div className="relative z-10 shrink-0 px-4 pb-4 pt-2 md:px-6 md:pb-6">
            <div className="mx-auto w-full max-w-3xl" style={{ viewTransitionName: 'caja-de-redes' }} {...seguirElFoco}>
              {caja}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative h-full overflow-y-auto px-4 md:px-6">
          {/* La demo y la caja a media altura, centradas; en el teléfono la caja va abajo, a
              mano del pulgar, con la demo en el lugar que sobra (los dos aires flexibles). */}
          <div className="relative -mx-4 flex min-h-full flex-col px-4 pb-4 pt-16 md:-mx-6 md:justify-center md:px-6 md:py-10">
            <div aria-hidden="true" className="flex-1 md:hidden" />
            <div className="crear-cambia relative z-10 mb-5 md:mb-8">
              <DemoDelAsistente minimizada={interactuando} rubro={rubroDeLaPortada} />
            </div>
            <div aria-hidden="true" className="flex-1 md:hidden" />
            {/* El ancho de la caja de Crear SIN el interruptor (1100 − 78 − el gap): la misma
                caja, no una más ancha. */}
            <div className="relative z-10 mx-auto w-full max-w-[1010px]" style={{ viewTransitionName: 'caja-de-redes' }} {...seguirElFoco}>
              {caja}
            </div>
          </div>
        </div>
      )}

      {/* El selector de Crear: lo elegido entra a los adjuntos del chat. */}
      <ModalDeFuentes abierto={fuentes !== null} onCerrar={() => setFuentes(null)} pestanaInicial={fuentes ?? 'productos'} subidaEnDosTarjetas />
    </div>
  );
};

export default PortadaDelAsistente;
